import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DiveTypeToggle from '../DiveTypeToggle'
import DepthTierSelect from '../DepthTierSelect'
import AddressAutocomplete from '../AddressAutocomplete'
import DiveSiteAutocomplete, {
  DiveSiteSelection
} from '../DiveSiteAutocomplete'
import { createDive, DiveServiceError } from '../../services/diveService'
import { useSessionStore } from '../../state'
import Database from '../../types/Database'

const DiveForm = () => {
  const navigate = useNavigate()
  const profile = useSessionStore(s => s.profile)
  const user = useSessionStore(s => s.user)

  const accreditationStatus = profile?.accreditation?.status ?? null
  const guidedDiveFlag = profile?.guidedDiveFlag ?? false

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [siteSelectedFromAutocomplete, setSiteSelectedFromAutocomplete] =
    useState(false)

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<Database.DiveFormInputs>({
    defaultValues: {
      type: 'buddy',
      siteName: '',
      description: '',
      location: null,
      dateTime: '',
      duration: undefined as unknown as number,
      depthTier: '' as unknown as Database.DiveFormInputs['depthTier'],
      price: undefined,
      maxParticipants: undefined,
      meetingPoint: '',
      notes: ''
    }
  })

  const diveType = watch('type')

  if (accreditationStatus !== 'verified') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='warning'>
          Verified accreditation is required to create a dive. Please complete
          your accreditation verification before creating a listing.
        </Alert>
      </Box>
    )
  }

  const onSubmit = async (data: Database.DiveFormInputs) => {
    if (!user) return

    setSubmitError(null)
    setIsSubmitting(true)

    try {
      await createDive(data, user.uid, accreditationStatus, guidedDiveFlag)
      navigate('/dives')
    } catch (error) {
      if (error instanceof DiveServiceError) {
        setSubmitError(error.message)
      } else {
        setSubmitError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{ p: 3 }}
    >
      <Stack spacing={3}>
        <Typography variant='h5' component='h1'>
          Create a Dive
        </Typography>

        {submitError && <Alert severity='error'>{submitError}</Alert>}

        <Controller
          name='type'
          control={control}
          render={({ field }) => (
            <DiveTypeToggle
              value={field.value}
              onChange={field.onChange}
              guidedDiveFlag={guidedDiveFlag}
            />
          )}
        />

        <Controller
          name='siteName'
          control={control}
          rules={{
            required: 'Site name is required',
            minLength: { value: 1, message: 'Site name is required' },
            maxLength: {
              value: 100,
              message: 'Site name must be at most 100 characters'
            }
          }}
          render={({ field }) => (
            <DiveSiteAutocomplete
              value={field.value}
              onChange={field.onChange}
              onSiteSelected={(selection: DiveSiteSelection) => {
                if (selection) {
                  setSiteSelectedFromAutocomplete(true)
                  if (selection.location) {
                    setValue('location', selection.location)
                  }
                  if (selection.description) {
                    setValue('description', selection.description)
                  }
                  if (selection.depthTier) {
                    setValue('depthTier', selection.depthTier)
                  }
                } else {
                  setSiteSelectedFromAutocomplete(false)
                }
              }}
              error={!!errors.siteName}
              helperText={errors.siteName?.message}
            />
          )}
        />

        <TextField
          label='Description'
          fullWidth
          multiline
          rows={3}
          error={!!errors.description}
          helperText={errors.description?.message}
          disabled={siteSelectedFromAutocomplete}
          {...register('description', {
            required: 'Description is required',
            minLength: { value: 1, message: 'Description is required' },
            maxLength: {
              value: 500,
              message: 'Description must be at most 500 characters'
            }
          })}
        />

        <Controller
          name='location'
          control={control}
          rules={{
            validate: value => value !== null || 'A dive location is required'
          }}
          render={({ field }) => (
            <AddressAutocomplete
              value={field.value}
              onChange={field.onChange}
              error={!!errors.location}
              helperText={errors.location?.message}
              disabled={siteSelectedFromAutocomplete}
            />
          )}
        />

        <TextField
          label='Date & Time'
          type='datetime-local'
          fullWidth
          error={!!errors.dateTime}
          helperText={errors.dateTime?.message}
          slotProps={{
            inputLabel: { shrink: true }
          }}
          {...register('dateTime', {
            required: 'Date and time is required',
            validate: value => {
              if (!value) return 'Date and time is required'
              const date = new Date(value)
              if (date <= new Date()) {
                return 'Date and time must be in the future'
              }
              return true
            }
          })}
        />

        <TextField
          label='Duration (minutes)'
          type='number'
          fullWidth
          error={!!errors.duration}
          helperText={errors.duration?.message}
          {...register('duration', {
            required: 'Duration is required',
            valueAsNumber: true,
            min: { value: 15, message: 'Duration must be at least 15 minutes' },
            max: {
              value: 600,
              message: 'Duration must be at most 600 minutes'
            }
          })}
        />

        <Controller
          name='depthTier'
          control={control}
          rules={{
            validate: value =>
              ['18m', '30m', '30m+'].includes(value) || 'Depth tier is required'
          }}
          render={({ field }) => (
            <DepthTierSelect
              value={field.value || ''}
              onChange={field.onChange}
              error={!!errors.depthTier}
              helperText={errors.depthTier?.message}
              disabled={siteSelectedFromAutocomplete}
            />
          )}
        />

        {diveType === 'guided' && (
          <TextField
            label='Price'
            type='number'
            fullWidth
            error={!!errors.price}
            helperText={errors.price?.message}
            {...register('price', {
              required: 'Price is required for guided dives',
              valueAsNumber: true,
              min: { value: 1, message: 'Price must be at least 1' },
              max: { value: 10000, message: 'Price must be at most 10000' }
            })}
          />
        )}

        {diveType === 'guided' && (
          <TextField
            label='Max Participants'
            type='number'
            fullWidth
            error={!!errors.maxParticipants}
            helperText={errors.maxParticipants?.message}
            {...register('maxParticipants', {
              required: 'Max participants is required for guided dives',
              valueAsNumber: true,
              min: {
                value: 1,
                message: 'Max participants must be at least 1'
              },
              max: {
                value: 10,
                message: 'Max participants must be at most 10'
              }
            })}
          />
        )}

        <TextField
          label='Meeting Point'
          fullWidth
          error={!!errors.meetingPoint}
          helperText={errors.meetingPoint?.message}
          {...register('meetingPoint', {
            required: 'Meeting point is required',
            minLength: { value: 1, message: 'Meeting point is required' },
            maxLength: {
              value: 200,
              message: 'Meeting point must be at most 200 characters'
            }
          })}
        />

        <TextField
          label='Notes (optional)'
          fullWidth
          multiline
          rows={2}
          error={!!errors.notes}
          helperText={errors.notes?.message}
          {...register('notes', {
            maxLength: {
              value: 500,
              message: 'Notes must be at most 500 characters'
            }
          })}
        />

        <Button
          type='submit'
          variant='contained'
          size='large'
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={20} color='inherit' /> : null
          }
        >
          {isSubmitting ? 'Creating...' : 'Create Dive'}
        </Button>

        <Button variant='outlined' size='large' onClick={() => navigate('/')}>
          Cancel
        </Button>
      </Stack>
    </Box>
  )
}

export default DiveForm
